/* eslint-disable */
import { useState, useEffect, useRef, useMemo } from "react";

// T-1015 — dev_number reverse-search detector. When the catalog search matches
// DA/DO/DS + digits (optional trailing letter), the filter branches into a
// reverse lookup against the linkedDevsMap (built by useLinkedDevsMap from the
// already-loaded devs array) instead of normal sf substring search.
export const DEV_NUMBER_RE = /^(DA|DO|DS)\s*\d+[A-Z]?$/i;

// Build a Supabase query with server-side filters applied
export function applyFilters(query, { brand, gender, type, material, status, shape, assigned, createdBy, search, linkedDevsMap }) {
  let q = query;
  if (brand && brand.size > 0) q = q.in("data->>brand", [...brand]);
  if (gender && gender.size > 0) q = q.in("data->>gender", [...gender]);
  if (type && type.size > 0) q = q.in("data->>frameCategory", [...type]);
  if (material && material.size > 0) q = q.in("data->>material", [...material]);
  if (status && status.size > 0) q = q.in("data->>status", [...status]);
  if (shape && shape.size > 0) {
    const clauses = [...shape].flatMap(s => [
      `data->>shape1.eq.${s}`, `data->>shape2.eq.${s}`, `data->>shape3.eq.${s}`
    ]);
    q = q.or(clauses.join(","));
  }
  if (assigned && assigned.size > 0) {
    const hasWH = assigned.has("Warehouse");
    const others = [...assigned].filter(a => a !== "Warehouse");
    if (hasWH && others.length > 0) {
      q = q.or(`data->>assignedTo.is.null,data->>assignedTo.eq.,data->>assignedTo.ilike.warehouse,${others.map(a=>`data->>assignedTo.eq.${a}`).join(",")}`);
    } else if (hasWH) {
      q = q.or("data->>assignedTo.is.null,data->>assignedTo.eq.,data->>assignedTo.ilike.warehouse");
    } else {
      q = q.in("data->>assignedTo", others);
    }
  }
  if (createdBy && createdBy.size > 0) q = q.in("data->>created_by_name", [...createdBy]);
  if (search) {
    const s = search.replace(/[.,()'"\\]/g, "").trim();
    if (s) {
      // T-1015 — reverse-search branch: dev_number patterns map to linked item IDs.
      // T-1031 (2026-05-14) — when no linked items match, fall through to the sf
      // substring branch so manual data.developmentOrder entries (the sibling
      // direction of T-1015's based_on_data.item_id link) are also caught.
      let didReverseMatch = false;
      if (DEV_NUMBER_RE.test(s) && linkedDevsMap) {
        const sUpper = s.toUpperCase().replace(/\s+/g, "");
        const matchingItemIds = [];
        for (const [itemId, devs] of linkedDevsMap.entries()) {
          for (const d of devs) {
            const devNum = (d.dev_number || d.data?.dev_number || "").toUpperCase();
            if (devNum.includes(sUpper)) {
              matchingItemIds.push(itemId);
              break;
            }
          }
        }
        if (matchingItemIds.length > 0) {
          q = q.in("id", matchingItemIds);
          didReverseMatch = true;
        }
        // else: linkedDevsMap had no match for this dev-number pattern —
        // fall through to sf substring branch below (T-1031 fix).
      }
      if (!didReverseMatch) {
        // Convention #52 — items search field array. `color` + `color_name` removed 2026-05-12
        // per T-1027 — verified 0 rows in items.data JSONB (LATERAL jsonb_object_keys).
        const sf = ["sku","brand","model","gender","material","frameCategory","notes","status","shape1","shape2","shape3","assignedTo","developmentOrder"];
        q = q.or(sf.map(f => `data->>${f}.ilike.%${s}%`).join(","));
      }
    }
  }
  return q;
}

// Apply server-side sort order to a Supabase query
export function applySortOrder(query, sort) {
  switch (sort) {
    case "oldest":   return query.order("created_at", { ascending: true });
    case "brand_az": return query.order("data->>brand", { ascending: true }).order("data->>sku", { ascending: true });
    case "brand_za": return query.order("data->>brand", { ascending: false }).order("data->>sku", { ascending: true });
    case "shape_az": return query.order("data->>shape1", { ascending: true }).order("data->>sku", { ascending: true });
    case "updated":  return query.order("updated_at", { ascending: false, nullsFirst: false }).order("created_at", { ascending: false });
    case "sku_az":   return query.order("data->>sku", { ascending: true });
    case "newest":
    default:         return query.order("created_at", { ascending: false });
  }
}

// Check if any filter is active
export function hasActiveFilters(f) {
  if (f.search) return true;
  for (const k of ["brand","gender","type","material","status","shape","assigned","createdBy"]) {
    const v = f[k];
    if (v && ((v instanceof Set && v.size > 0) || (Array.isArray(v) && v.length > 0))) return true;
  }
  return false;
}

// Hook: manages all catalog filter state.
// T-1015 — filtersRef.current.linkedDevsMap is injected by App.js post-mount
// (after useLinkedDevsMap computes the Map from useAppData.devs). Because of
// hook-ordering constraints (useCatalogFilters must run before useAppData,
// which produces devs), this hook itself does NOT receive linkedDevsMap.
// Instead its useEffect spread-merges into filtersRef.current, preserving
// any externally-injected fields (like linkedDevsMap) across filter changes.
export function useCatalogFilters() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 220); return () => clearTimeout(t); }, [search]);

  const [fBrand, setFBrand] = useState(new Set());
  const [fGender, setFGender] = useState(new Set());
  const [fType, setFType] = useState(new Set());
  const [fMat, setFMat] = useState(new Set());
  const [fStatus, setFStatus] = useState(new Set());
  const [fShape, setFShape] = useState(new Set());
  const [fAssigned, setFAssigned] = useState(new Set());
  const [fCreatedBy, setFCreatedBy] = useState(new Set());
  const [sortBy, setSortBy] = useState("newest");

  const filtersRef = useRef({});
  useEffect(() => {
    filtersRef.current = { ...filtersRef.current, brand: fBrand, gender: fGender, type: fType, material: fMat, status: fStatus, shape: fShape, assigned: fAssigned, createdBy: fCreatedBy, search: debouncedSearch, sortBy };
  }, [fBrand, fGender, fType, fMat, fStatus, fShape, fAssigned, fCreatedBy, debouncedSearch, sortBy]);

  const clearAll = () => {
    setFBrand(new Set()); setFGender(new Set()); setFType(new Set()); setFMat(new Set());
    setFShape(new Set()); setFStatus(new Set()); setFAssigned(new Set()); setFCreatedBy(new Set());
  };

  const activeFilterCount = [fBrand, fGender, fType, fMat, fShape, fStatus, fAssigned, fCreatedBy].reduce((n, s) => n + s.size, 0);

  const isCurrentlyFiltered = useMemo(() => hasActiveFilters({
    brand: fBrand, gender: fGender, type: fType, material: fMat,
    status: fStatus, shape: fShape, assigned: fAssigned, createdBy: fCreatedBy,
    search: debouncedSearch
  }), [fBrand, fGender, fType, fMat, fStatus, fShape, fAssigned, fCreatedBy, debouncedSearch]);

  return {
    search, setSearch, debouncedSearch,
    fBrand, setFBrand, fGender, setFGender, fType, setFType, fMat, setFMat,
    fStatus, setFStatus, fShape, setFShape, fAssigned, setFAssigned, fCreatedBy, setFCreatedBy,
    sortBy, setSortBy,
    filtersRef, clearAll, activeFilterCount, isCurrentlyFiltered,
  };
}
